import { useState, useRef } from 'react'

const SCORE_COLORS = {
  CLEAN: '#22c55e',
  MILD: '#84cc16',
  NOTICEABLE: '#eab308',
  OBVIOUS: '#f97316',
  BLATANT: '#ef4444',
}

const LABEL_DESCRIPTIONS = {
  CLEAN: 'Looks human-written',
  MILD: 'Few AI traits detected',
  NOTICEABLE: 'Some AI patterns present',
  OBVIOUS: 'Clearly AI-generated',
  BLATANT: 'Very likely AI-generated',
}

const AiFingerprintSection = () => {
  const [text, setText] = useState('')
  const [result, setResult] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [dragActive, setDragActive] = useState(false)
  const fileInputRef = useRef(null)

  const handleFile = (file) => {
    if (!file) return
    const reader = new FileReader()
    reader.onload = (e) => setText(e.target.result)
    reader.readAsText(file)
  }

  const handleDrop = (e) => {
    e.preventDefault()
    setDragActive(false)
    const file = e.dataTransfer.files[0]
    if (file) handleFile(file)
  }

  const handleDragOver = (e) => {
    e.preventDefault()
    setDragActive(true)
  }

  const handleAnalyze = async () => {
    if (!text.trim()) return
    setLoading(true)
    setError(null)
    setResult(null)
    try {
      const res = await fetch('/api/analyze', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text }),
      })
      if (!res.ok) {
        const data = await res.json().catch(() => null)
        throw new Error(data?.detail || `Server error (${res.status})`)
      }
      setResult(await res.json())
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const scoreColor = result ? SCORE_COLORS[result.label] || '#6366f1' : null

  return (
    <section id="ai-fingerprint" className="ai-fingerprint-section">
      <div className="container">
        <h2 className="section-title">AI Text Fingerprint</h2>
        <p className="section-subtitle">
          Detect AI-generated writing by analyzing linguistic patterns.
          Upload a text file or paste text to get an analysis.
        </p>

        <div className="ai-fingerprint-content">
          <div
            className={`ai-fingerprint-upload${dragActive ? ' drag-active' : ''}`}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={() => setDragActive(false)}
          >
            <textarea
              className="ai-fingerprint-textarea"
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Paste text here or drag a file..."
              rows={8}
            />
            <div className="ai-fingerprint-actions">
              <button
                className="btn-upload"
                onClick={() => fileInputRef.current?.click()}
              >
                Upload File
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".txt,.md,.text"
                style={{ display: 'none' }}
                onChange={(e) => handleFile(e.target.files[0])}
              />
              <button
                className="btn-analyze"
                onClick={handleAnalyze}
                disabled={loading || !text.trim()}
              >
                {loading ? 'Analyzing...' : 'Analyze'}
              </button>
            </div>
          </div>

          {error && <div className="ai-fingerprint-error">{error}</div>}

          {result && (
            <div className="ai-fingerprint-results">
              <div className="ai-fingerprint-score" style={{ borderColor: scoreColor }}>
                <span className="score-number" style={{ color: scoreColor }}>
                  {result.score}
                </span>
                <span className="score-label" style={{ color: scoreColor }}>
                  {result.label}
                </span>
                <span className="score-desc">
                  {LABEL_DESCRIPTIONS[result.label]}
                </span>
              </div>

              <div className="ai-fingerprint-categories">
                <h3>Category Breakdown</h3>
                {result.categories.map((cat) => (
                  <div key={cat.name} className="category-row">
                    <div className="category-header">
                      <span className="category-name">
                        {cat.name.replace('_', ' ')}
                      </span>
                      <span className="category-weight">{cat.weight}%</span>
                    </div>
                    <div className="category-bar">
                      <div
                        className="category-bar-fill"
                        style={{
                          width: `${Math.round(cat.raw_score * 100)}%`,
                          backgroundColor: scoreColor,
                        }}
                      />
                    </div>
                    {cat.hits.length > 0 && (
                      <details className="category-hits">
                        <summary>{cat.hits.length} finding{cat.hits.length !== 1 ? 's' : ''}</summary>
                        <ul>
                          {cat.hits.map((hit, i) => (
                            <li key={i}>{hit.trim()}</li>
                          ))}
                        </ul>
                      </details>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  )
}

export default AiFingerprintSection
